import { computed, DestroyRef, effect, inject, Injectable, signal } from '@angular/core';
import { IntegrationModelResponseDto } from '../models';
import { IntegrationsService } from './integrations.service';
import { LocalStorageService } from './local-storage.service';
import { PrinterConnectionService } from './printer-connection.service';
import { PrinterDiscoveryService } from './printer-discovery.service';
import { PrinterManagementService } from './printer-management.service';
import { VenueService } from './venue.service';

/**
 * Known integration IDs that have corresponding sub-services
 */
export type ManagedIntegrationId = 'printer-discovery' | 'printers';

/**
 * Interface for sub-services that can be managed by the orchestrator
 */
export interface ManagedIntegrationService {
  /** Initialize the service with its settings */
  initialize(settings: Record<string, unknown>): Promise<void>;
  /** Cleanup and stop all operations */
  destroy(): Promise<void>;
  /** Whether the service's underlying API is supported */
  readonly isSupported: () => boolean;
}

/** Storage key for persisting last connected printer */
const LAST_PRINTER_KEY = 'lastConnectedPrinter';

/**
 * Central orchestrator for managing integration sub-services.
 *
 * Responsibilities:
 * - Initialize sub-services on app startup when enabled
 * - Re-initialize when venue changes
 * - Handle toggle on/off of integrations
 * - Auto-disable unsupported integrations
 * - Persist and restore state (e.g., last connected printer)
 */
@Injectable({ providedIn: 'root' })
export class IntegrationsOrchestratorService {
  readonly #integrationsService = inject(IntegrationsService);
  readonly #venueService = inject(VenueService);
  readonly #localStorageService = inject(LocalStorageService);
  readonly #printerDiscoveryService = inject(PrinterDiscoveryService);
  readonly #printerConnectionService = inject(PrinterConnectionService);
  readonly #printerManagementService = inject(PrinterManagementService);
  readonly #destroyRef = inject(DestroyRef);

  /** Track which integrations are currently active/initialized */
  readonly #activeIntegrations = signal<Set<ManagedIntegrationId>>(new Set());

  /** Track initialization status */
  readonly #isInitializing = signal<boolean>(false);
  readonly #initializationErrors = signal<Map<ManagedIntegrationId, string>>(new Map());

  /** Public readonly signals */
  readonly activeIntegrations = computed(() => Array.from(this.#activeIntegrations()));
  readonly isInitializing = this.#isInitializing.asReadonly();
  readonly initializationErrors = this.#initializationErrors.asReadonly();

  /** Map of integration IDs to their corresponding services */
  readonly #serviceMap: Record<ManagedIntegrationId, ManagedIntegrationService> = {
    'printer-discovery': this.#createPrinterDiscoveryAdapter(),
    printers: this.#createPrintersAdapter(),
  };

  constructor() {
    // Watch for venue changes and re-initialize integrations
    effect(() => {
      const venueId = this.#venueService.selectedVenueId();
      if (venueId) {
        // Venue changed, reinitialize all enabled integrations
        this.initializeAllEnabled();
      } else {
        // No venue selected, stop all integrations
        this.destroyAll();
      }
    });

    // Watch for integrations data loading completion
    effect(() => {
      const integrations = this.#integrationsService.integrations();
      const loading = this.#integrationsService.loading();

      if (!loading && integrations.length > 0) {
        // Integrations loaded, initialize enabled ones
        this.initializeAllEnabled();
      }
    });
  }

  /**
   * Initialize all enabled integrations.
   * Called on app startup and venue change.
   */
  async initializeAllEnabled(): Promise<void> {
    const integrations = this.#integrationsService.integrations();
    const enabledIntegrations = integrations.filter((i) => i.isEnabled);

    this.#isInitializing.set(true);
    this.#initializationErrors.update((m) => {
      m.clear();
      return new Map(m);
    });

    try {
      // First, destroy any that are no longer enabled
      const enabledIds = new Set(enabledIntegrations.map((i) => i.integrationId as ManagedIntegrationId));
      const currentActive = this.#activeIntegrations();

      for (const activeId of currentActive) {
        if (!enabledIds.has(activeId)) {
          await this.destroyIntegration(activeId);
        }
      }

      // Then initialize all enabled integrations
      await Promise.all(enabledIntegrations.map((integration) => this.initializeIntegration(integration)));
    } finally {
      this.#isInitializing.set(false);
    }
  }

  /**
   * Initialize a specific integration by its model.
   * Called when an integration is toggled ON.
   */
  async initializeIntegration(integration: IntegrationModelResponseDto): Promise<boolean> {
    const integrationId = integration.integrationId as ManagedIntegrationId;
    const service = this.#serviceMap[integrationId];

    if (!service) {
      // Not a managed integration, nothing to do
      return true;
    }

    // Check if already active
    if (this.#activeIntegrations().has(integrationId)) {
      return true;
    }

    // Check support
    if (!service.isSupported()) {
      const errorMsg = `${integration.name} is not supported in this browser`;
      this.#initializationErrors.update((m) => {
        m.set(integrationId, errorMsg);
        return new Map(m);
      });

      // Auto-disable the integration
      await this.#autoDisableIntegration(integration);
      return false;
    }

    try {
      await service.initialize(integration.settings || {});

      this.#activeIntegrations.update((set) => {
        set.add(integrationId);
        return new Set(set);
      });

      return true;
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Initialization failed';
      this.#initializationErrors.update((m) => {
        m.set(integrationId, errorMsg);
        return new Map(m);
      });

      // Auto-disable on failure
      await this.#autoDisableIntegration(integration);
      return false;
    }
  }

  /**
   * Destroy/stop a specific integration.
   * Called when an integration is toggled OFF.
   */
  async destroyIntegration(integrationId: ManagedIntegrationId): Promise<void> {
    const service = this.#serviceMap[integrationId];

    if (!service) {
      return;
    }

    if (!this.#activeIntegrations().has(integrationId)) {
      return;
    }

    try {
      await service.destroy();
    } finally {
      this.#activeIntegrations.update((set) => {
        set.delete(integrationId);
        return new Set(set);
      });
    }
  }

  /**
   * Destroy all active integrations.
   * Called when venue is deselected or on app shutdown.
   */
  async destroyAll(): Promise<void> {
    const activeIds = Array.from(this.#activeIntegrations());
    await Promise.all(activeIds.map((id) => this.destroyIntegration(id)));
  }

  /**
   * Handle integration toggle - called by IntegrationsService after API update
   */
  async handleIntegrationToggle(integration: IntegrationModelResponseDto): Promise<void> {
    const integrationId = integration.integrationId as ManagedIntegrationId;

    if (integration.isEnabled) {
      await this.initializeIntegration(integration);
    } else {
      await this.destroyIntegration(integrationId);
    }
  }

  /**
   * Check if an integration is supported
   */
  isIntegrationSupported(integrationId: ManagedIntegrationId): boolean {
    const service = this.#serviceMap[integrationId];
    return service ? service.isSupported() : true;
  }

  /**
   * Get the error message for an integration, if any
   */
  getIntegrationError(integrationId: ManagedIntegrationId): string | undefined {
    return this.#initializationErrors().get(integrationId);
  }

  /**
   * Auto-disable an integration via API
   */
  async #autoDisableIntegration(integration: IntegrationModelResponseDto): Promise<void> {
    if (integration.isEnabled) {
      try {
        await this.#integrationsService.toggleIntegrationStatus(integration._id);
      } catch (error) {
        console.error(`Failed to auto-disable integration ${integration.integrationId}:`, error);
      }
    }
  }

  /**
   * Create an adapter for PrinterDiscoveryService that implements ManagedIntegrationService
   */
  #createPrinterDiscoveryAdapter(): ManagedIntegrationService {
    return {
      isSupported: () => this.#printerDiscoveryService.isSupported(),

      initialize: async (settings: Record<string, unknown>) => {
        const autoConnect = (settings['autoConnect'] as boolean) ?? true;
        const preferredProtocol = (settings['preferredProtocol'] as string) ?? 'mdns';

        // Start scanning for printers
        await this.#printerDiscoveryService.startScan();

        // If autoConnect is enabled, try to reconnect to last used printer
        if (autoConnect) {
          const lastPrinter = this.#getLastConnectedPrinter();
          if (lastPrinter) {
            // Wait a bit for scan results, then try to connect
            setTimeout(async () => {
              const discovered = this.#printerDiscoveryService.discoveredPrinters();
              const matchingPrinter = discovered.find((p) => p.address === lastPrinter.address && p.port === lastPrinter.port);

              if (matchingPrinter) {
                await this.#printerDiscoveryService.connect(matchingPrinter);
              }
            }, 3000); // Wait 3 seconds for discovery
          }
        }
      },

      destroy: async () => {
        // Save current printer before disconnecting
        const currentPrinter = this.#printerDiscoveryService.connectedPrinter();
        if (currentPrinter) {
          this.#saveLastConnectedPrinter(currentPrinter);
        }

        // Stop scanning and disconnect
        await this.#printerDiscoveryService.stopScan();
        await this.#printerDiscoveryService.disconnect();
      },
    };
  }

  /**
   * Save the last connected printer to localStorage
   */
  #saveLastConnectedPrinter(printer: { address: string; port: number; name?: string }): void {
    const venueId = this.#venueService.selectedVenueId();
    if (!venueId) return;

    const key = `${LAST_PRINTER_KEY}_${venueId}`;
    this.#localStorageService.savePlainData(key, {
      address: printer.address,
      port: printer.port,
      name: printer.name,
    });
  }

  /**
   * Get the last connected printer from localStorage
   */
  #getLastConnectedPrinter(): { address: string; port: number; name?: string } | null {
    const venueId = this.#venueService.selectedVenueId();
    if (!venueId) return null;

    const key = `${LAST_PRINTER_KEY}_${venueId}`;
    return this.#localStorageService.getPlainData<{ address: string; port: number; name?: string }>(key);
  }

  /**
   * Create an adapter for PrinterManagementService (new unified printers integration)
   */
  #createPrintersAdapter(): ManagedIntegrationService {
    return {
      // Printers integration is always supported (WebUSB + manual IP work in most browsers)
      isSupported: () => true,

      initialize: async (_settings: Record<string, unknown>) => {
        // Settings are already synced via PrinterManagementService effect
        // Just refresh to ensure we have latest data
        this.#printerManagementService.refresh();
      },

      destroy: async () => {
        // Disconnect all printers
        const printers = this.#printerManagementService.printers();
        for (const printer of printers) {
          await this.#printerConnectionService.disconnect(printer.id);
        }
      },
    };
  }
}
